import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AuthenticatedLayout } from '@/components/layout/AuthenticatedLayout';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { CreateVideoPage } from '@/features/create/CreateVideoPage';
import { TemplateSelectionPage } from '@/features/create/TemplateSelectionPage';
import { ProjectEditorPage } from '@/features/create/ProjectEditorPage';
import { CustomOrderPage } from '@/features/create/CustomOrderPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { RegisterPage } from '@/features/auth/RegisterPage';
import { ConfirmEmailPage } from '@/features/auth/ConfirmEmailPage';
import { ExternalAuthCallbackPage } from '@/features/auth/ExternalAuthCallbackPage';
import { ScreensPage } from '@/features/screens/ScreensPage';
import { PlansPage } from '@/features/billing/PlansPage';
import { PlaceholderPage } from '@/features/PlaceholderPage';
import { DiffusionPage } from '@/features/diffusion/DiffusionPage';
import { SupportPage } from '@/features/support/SupportPage';
import { TransactionsPage } from '@/features/billing/TransactionsPage';
import { ParametresPage } from '@/features/settings/ParametresPage';
import { VideosPage } from '@/features/videos/VideosPage';
import { ExploitsPage } from '@/features/exploits/ExploitsPage';
import { MessagesPage } from '@/features/messages/MessagesPage';
import { NotificationsPage } from '@/features/notifications/NotificationsPage';
import { useAuthStore } from '@/stores/auth-store';

const rootRoute = createRootRoute({
  component: Outlet,

  notFoundComponent: () => (
    <PlaceholderPage
      title="Page introuvable"
      description="Le lien que vous avez suivi ne correspond à aucune page. Revenez au tableau de bord pour reprendre votre travail."
    />
  ),
});

/**
 * Garde d'authentification.
 *
 * Attend la fin de la restauration de session (`isBootstrapped`) avant de décider.
 * Sans cette attente, un simple rechargement de page redirigerait vers la connexion :
 * l'access token vit en mémoire, il est donc toujours absent au premier rendu, le temps
 * que l'appel à `/auth/refresh` aboutisse.
 *
 * À NOTER : c'est du confort d'interface, pas de la sécurité. Un utilisateur peut modifier
 * l'état du navigateur pour afficher n'importe quelle page ; il n'en obtiendra aucune
 * donnée, l'API refusant toute requête sans jeton valide.
 */
function requireAuth({ location }: { location: { href: string } }) {
  const { accessToken, isBootstrapped } = useAuthStore.getState();

  if (isBootstrapped && accessToken === null) {
    throw redirect({
      to: '/connexion',

      // La destination initiale est conservée pour y revenir après connexion.
      search: { redirect: location.href },
    });
  }
}

// ============================================================================
// Routes publiques
// ============================================================================

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connexion',
  component: LoginPage,

  // L'objet est construit CONDITIONNELLEMENT : avec `exactOptionalPropertyTypes`, écrire
  // `{ redirect: undefined }` rendrait la propriété obligatoire pour tous les <Link>.
  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search.redirect === 'string' ? { redirect: search.redirect } : {},
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inscription',
  component: RegisterPage,
});

/**
 * Confirmation d'adresse par code.
 *
 * Route publique : le compte existe mais ne peut pas encore se connecter, aucune session
 * n'est donc disponible pour protéger cet écran. L'adresse transite par la query string
 * afin que la page survive à un rechargement — sans quoi la personne se retrouve sans
 * aucun moyen d'aller au bout de son inscription.
 */
const confirmEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/confirmation-email',
  component: ConfirmEmailPage,

  // Repli sur la chaîne vide plutôt que sur `undefined` : la page a besoin d'une valeur
  // pour son type, et affiche d'elle-même quoi faire quand l'adresse manque.
  validateSearch: (search: Record<string, unknown>): { email: string } => ({
    email: typeof search.email === 'string' ? search.email : '',
  }),
});

/**
 * Retour d'un fournisseur d'identité externe.
 *
 * Le chemin est FIXE et déclaré tel quel chez le fournisseur (`RedirectUri` côté API) : il
 * ne peut donc pas porter le contexte de la demande, qui est relu depuis `sessionStorage`.
 * Route publique, comme la connexion : au retour d'une connexion externe, aucune session
 * n'existe encore.
 */
const externalAuthCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connexion/retour/$provider',
  component: ExternalAuthCallbackPage,

  // Tout est optionnel : le fournisseur renvoie soit `code` + `state`, soit `error` quand
  // la personne refuse le consentement. Chaque propriété est ajoutée CONDITIONNELLEMENT,
  // `exactOptionalPropertyTypes` interdisant d'écrire `{ code: undefined }`.
  validateSearch: (search: Record<string, unknown>): { code?: string; state?: string; error?: string } => ({
    ...(typeof search.code === 'string' ? { code: search.code } : {}),
    ...(typeof search.state === 'string' ? { state: search.state } : {}),
    ...(typeof search.error === 'string' ? { error: search.error } : {}),
  }),
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mot-de-passe-oublie',
  component: () => (
    <PlaceholderPage
      title="Mot de passe oublié"
      description="L'endpoint /auth/password-reset/request est disponible côté API et couvert par les tests d'intégration ; l'écran reste à câbler."
    />
  ),
});

// ============================================================================
// Routes authentifiées
// ============================================================================

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: requireAuth,
  component: AuthenticatedLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: DashboardPage,
});

const screensRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ecrans',
  component: ScreensPage,
});

const plansRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/offres',
  component: PlansPage,
});

const createVideoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/creer',
  component: CreateVideoPage,
});

const templateSelectionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/creer/template',
  component: TemplateSelectionPage,
});

const projectEditorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/creer/template/$projectId',
  component: ProjectEditorPage,
});

const customOrderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/creer/sur-mesure',
  component: CustomOrderPage,
});

const diffusionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/diffusion',
  component: DiffusionPage,
});

const supportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/support',
  component: SupportPage,
});

const transactionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/transactions',
  component: TransactionsPage,

  // Destination du retour Stripe Checkout après paiement : `checkout=success` déclenche le
  // bandeau de confirmation le temps que le webhook applique l'abonnement (asynchrone,
  // voir ProcessStripeWebhookEventCommand côté API).
  validateSearch: (search: Record<string, unknown>): { checkout?: 'success' } =>
    search.checkout === 'success' ? { checkout: 'success' } : {},
});

const parametresRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/parametres',
  component: ParametresPage,
});

const videosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/videos',
  component: VideosPage,
});

const exploitsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/exploits',
  component: ExploitsPage,
});

const messagesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/messages',
  component: MessagesPage,
});

const notificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/notifications',
  component: NotificationsPage,
});

/**
 * Routes dont l'API existe et est testée, mais dont l'écran reste à écrire.
 *
 * Les déclarer explicitement plutôt que de les omettre : la navigation reflète ainsi le
 * périmètre fonctionnel réel, et le reste à faire est visible plutôt que masqué.
 */
const placeholderRoutes = (
  [
    ['/regie', 'Régie', 'Mise à disposition d\'écrans inutilisés pour des campagnes de marques. API : champ isAvailableForAdvertising des écrans.'],
  ] as const
).map(([path, title, description]) =>
  createRoute({
    getParentRoute: () => appRoute,
    path,
    component: () => <PlaceholderPage title={title} description={description} />,
  }),
);

const routeTree = rootRoute.addChildren([
  loginRoute,
  registerRoute,
  confirmEmailRoute,
  externalAuthCallbackRoute,
  forgotPasswordRoute,
  appRoute.addChildren([
    dashboardRoute,
    screensRoute,
    plansRoute,
    createVideoRoute,
    templateSelectionRoute,
    projectEditorRoute,
    customOrderRoute,
    diffusionRoute,
    supportRoute,
    transactionsRoute,
    parametresRoute,
    videosRoute,
    exploitsRoute,
    messagesRoute,
    notificationsRoute,
    ...placeholderRoutes,
  ]),
]);

export const router = createRouter({
  routeTree,

  // Le défilement repart en haut à chaque navigation, comme un document classique.
  scrollRestoration: true,

  defaultPreload: 'intent',
});

// Enregistrement du type du routeur : c'est ce qui rend `<Link to="...">` et
// `params` vérifiés à la COMPILATION. Un lien vers une route inexistante devient une
// erreur de build au lieu d'un 404 découvert en production.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
